import { capitalize, numberToCurrency } from "@utils/string";
import { getDaysInMonth } from "@utils/date";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useAdmin } from "@utils/ModeContext";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import useDataStore from "@state/DataStore";
import { BoatType, ReservationType, SettingType } from "src/types/types";
import Button from "./Button";
import Description from "./Description";

export type CalendarProps = {
  boats?: BoatType[];
  reservations?: ReservationType[];
  settings?: SettingType[];
  allowedMonths: number[];
  selectedMonth: number;
  header?: React.ReactNode;
  currentYear: number;
  onMonthChange?: (month: number) => void;
  onReservationClick?: (date: Date, boatId: number) => void;
  onSettingsClick?: (date: Date, boatId: number) => void;
  onDetailClick?: (id: string | number) => void;
};

const Calendar = ({
  boats = [],
  reservations = [],
  settings = [],
  allowedMonths,
  selectedMonth,
  header,
  currentYear,
  onMonthChange,
  onReservationClick,
  onSettingsClick,
  onDetailClick,
}: CalendarProps): JSX.Element => {
  const isAdmin = useAdmin();
  const { t, i18n } = useTranslation();

  const [positionTop, setPositionTop] = useState(0);
  const discount = useDataStore((store) => {
    if (
      store.configuration.discountEnd &&
      new Date(store.configuration.discountEnd).getTime() >
        new Date().getTime() &&
      store.configuration.discount
    ) {
      return store.configuration.discount;
    }
    return undefined;
  });

  const onScroll = useCallback(() => {
    const div = document.querySelector("#top-menu-container .sticky");
    if (div instanceof HTMLElement) {
      const { height } = div.getBoundingClientRect();
      setPositionTop(height);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [onScroll]);

  const onMonthChangeHanlder: React.ChangeEventHandler<HTMLSelectElement> = ({
    target: { value },
  }) => {
    onMonthChange?.(parseInt(value, 10));
  };

  const mondays = useMemo(
    () => getDaysInMonth(6, selectedMonth, currentYear),
    [selectedMonth, currentYear]
  );

  const canGoBack = useMemo(() => {
    const index = allowedMonths.indexOf(selectedMonth);
    return index > 0;
  }, [allowedMonths, selectedMonth]);

  const canGoForward = useMemo(() => {
    const index = allowedMonths.indexOf(selectedMonth);
    return index < allowedMonths.length - 1;
  }, [allowedMonths, selectedMonth]);

  const onGoBack = () => {
    const index = allowedMonths.indexOf(selectedMonth);
    if (index > 0) {
      onMonthChange?.(allowedMonths[index - 1]);
    }
  };

  const onGoForward = () => {
    const index = allowedMonths.indexOf(selectedMonth);
    if (index < allowedMonths.length - 1) {
      onMonthChange?.(allowedMonths[index + 1]);
    }
  };

  const getCellContent = (date: Date, boatId: number) => {
    const res = reservations.find(
      (r) => r.boatId === boatId && dayjs(r.date).isSame(date, "day")
    );

    const setting = settings.find(
      (s) => s.boatId === boatId && dayjs(s.date).isSame(date, "day")
    );

    const boat = boats.find((b) => b.id === boatId);

    if (boat == null) {
      throw new Error(t<string>("boatNotFound", { boatId }));
    }

    const price = setting?.price ?? boat.price;
    const sale = setting?.sale;
    const disabled = setting?.disabled;

    const buttons: React.ReactNode[] = [];
    const states: React.ReactNode[] = [];

    if ((sale || discount) && !disabled) {
      states.push(
        <div key="sale">
          <span className="bg-red-100 text-red-800 text-sm mr-2 font-medium px-2.5 py-0.5 rounded">
            {sale && numberToCurrency(sale)}
            {sale == null &&
              discount &&
              numberToCurrency(price - (price / 100) * discount)}
          </span>
          <span className="line-through decoration-gray-500 bg-gray-100 text-gray-800 text-sm px-2.5 py-0.5 rounded">
            {numberToCurrency(price)}
          </span>
        </div>
      );
    }

    if (!sale && !discount && !disabled) {
      states.push(
        <div
          key="price"
          className={classNames(
            {
              "bg-blue-100 text-blue-800": res,
              "bg-green-100 text-green-800": !res,
            },
            "text-sm font-medium px-2.5 py-0.5 rounded"
          )}
        >
          {numberToCurrency(price)}
        </div>
      );
    }

    if (res && res.status === "draft" && !disabled) {
      states.push(
        <span
          key="state"
          className="bg-yellow-100 text-yellow-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded"
        >
          {t<string>("Waiting for approval")}
        </span>
      );
    }

    if (res && isAdmin && res.status === "publish" && !disabled) {
      states.push(
        <span
          key="state"
          className="bg-green-100 text-green-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded"
        >
          {t<string>("Confirmed")}
        </span>
      );
    }

    if (res && isAdmin && !disabled) {
      buttons.push(
        <Button
          key="detail"
          onClick={() => onDetailClick?.(res.id)}
          size="small"
        >
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
            {t<string>("Booking detail")}
          </div>
        </Button>
      );
    }

    if (res && res.status === "publish" && !isAdmin && !disabled) {
      states.push(
        <span
          className="bg-gray-100 text-gray-800 text-sm font-medium px-2.5 py-0.5 rounded"
          key="booked"
        >
          {t<string>("Booked")}
        </span>
      );
    }

    if (disabled) {
      states.push(
        <span
          className="bg-gray-100 text-gray-800 text-sm font-medium px-2.5 py-0.5 rounded"
          key="unavailable"
        >
          {t<string>("Unavailable")}
        </span>
      );
    }

    if (!res && !disabled) {
      buttons.push(
        <Button
          key="reservation"
          importance="primary"
          onClick={() => {
            onReservationClick?.(date, boatId);
          }}
          size="small"
          title={isAdmin ? t<string>("Book") : undefined}
          className="bg-green-700 hover:bg-green-800 focus:ring-green-300"
        >
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
              />
            </svg>
            {!isAdmin ? t<string>("Book") : undefined}
          </div>
        </Button>
      );
    }

    if (!res && isAdmin) {
      buttons.push(
        <Button
          key="settings"
          onClick={() => onSettingsClick?.(date, boatId)}
          size="small"
          outline
          title={!disabled ? t<string>("Settings") : undefined}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {disabled && t<string>("Settings")}
        </Button>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="inline-flex flex-col gap-2 justify-center items-center">
          {states}
        </div>
        <div className="inline-flex flex-row flex-wrap gap-2 justify-center items-center">
          {buttons}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-row gap-2 justify-center mb-4">
        <Button onClick={onGoBack} disabled={!canGoBack} importance="secondary">
          <svg
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
        </Button>
        <select
          onChange={onMonthChangeHanlder}
          value={selectedMonth}
          className="!max-w-[200px] !bg-gray-50 border !border-gray-300 !text-gray-900 !text-sm !rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full !p-2.5"
        >
          {allowedMonths.map((m) => (
            <option key={m} value={m}>
              {(() => {
                const date = new Date();
                date.setDate(1);
                date.setMonth(m);
                return capitalize(
                  date.toLocaleString(i18n.language, {
                    month: "long",
                  })
                );
              })()}{" "}
              {currentYear}
            </option>
          ))}
        </select>
        <Button
          onClick={onGoForward}
          disabled={!canGoForward}
          importance="secondary"
        >
          <svg
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
          </svg>
        </Button>
      </div>
      {header}
      <div className="relative shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-center sticky shadow-md bg-gray-50 top-0"
                style={{ top: positionTop }}
              >
                {"\u00A0"}
              </th>
              {mondays.map((monday) => (
                <th
                  className="px-6 py-3 text-center sticky shadow-md bg-gray-50 top-0"
                  key={monday.getDate()}
                  style={{ top: positionTop }}
                >{`${dayjs(monday).format("D.M.")} — ${dayjs(monday)
                  .add(7, "day")
                  .format("D.M.")}`}</th>
              ))}
            </tr>
          </thead>
          {boats.length > 0 ? (
            <tbody>
              {boats.map(({ name, id, model }, key) => (
                <tr
                  className={
                    key % 2 === 0 ? "bg-white border-b" : "border-b bg-gray-50"
                  }
                  key={id}
                >
                  <th
                    scope="row"
                    className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      {name}
                      {model && (
                        <Description text={model} className="inline-block" />
                      )}
                    </div>
                  </th>
                  {mondays.map((monday) => (
                    <td
                      className="px-6 py-4 text-center align-middle"
                      key={monday.getDate()}
                    >
                      {getCellContent(monday, id)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr className="h-[150px]">
                <td
                  className="px-6 py-4 text-center align-middle"
                  colSpan={mondays.length + 1}
                >
                  {t<string>("No data")}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default Calendar;
